<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("PLACEHOLDERS.provider_data") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #ef3405">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form>
        <div class="row">
          <!-- Provider Personal Data Section -->
          <div class="col-12 mb-3">
            <h5 class="section-title">
              {{ $t("PLACEHOLDERS.provider_personal_data") }}
            </h5>
          </div>

          <!-- Name -->
          <base-input
            col="4"
            type="text"
            :placeholder="$t('PLACEHOLDERS.full_name')"
            v-model.trim="data.name"
            disabled
          />

          <!-- Mobile -->
          <base-input
            col="4"
            type="text"
            :placeholder="$t('PLACEHOLDERS.phone')"
            v-model.trim="data.mobile"
            disabled
          />

          <!-- Email -->
          <base-input
            col="4"
            type="email"
            :placeholder="$t('PLACEHOLDERS.email')"
            v-model.trim="data.email"
            disabled
          />

          <!-- Join Date -->
          <base-input
            col="4"
            type="text"
            :placeholder="$t('PLACEHOLDERS.join_date')"
            v-model.trim="data.created_at"
            disabled
          />

          <!-- Avatar -->
          <base-image-upload-input
            col="12"
            identifier="avatar"
            :placeholder="$t('PLACEHOLDERS.avatar')"
            :preSelectedImage="data.avatar"
            disabled
          />

          <!-- Warehouse/Company Data Section -->
          <div class="col-12 mb-3 mt-4">
            <h5 class="section-title">
              {{ $t("PLACEHOLDERS.warehouse_company_data") }}
            </h5>
          </div>

          <!-- Activity Type -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.activity_type')"
            v-model="data.activity_type"
            disabled
          />

          <!-- Warehouse Address -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.warehouse_address')"
            v-model.trim="data.warehouse_address"
            disabled
          />

          <!-- Address -->
          <base-input
            col="12"
            type="text"
            :placeholder="$t('PLACEHOLDERS.address')"
            v-model.trim="data.address"
            disabled
          />

          <!-- Commercial Registration Image -->
          <base-image-upload-input
            v-if="data.commercial_registration_image"
            col="6"
            identifier="commercial_registration"
            :placeholder="$t('PLACEHOLDERS.commercial_register_image')"
            :preSelectedImage="data.commercial_registration_image"
            disabled
          />

          <!-- Tax Registration Image -->
          <base-image-upload-input
            v-if="data.tax_registration_image"
            col="6"
            identifier="tax_registration"
            :placeholder="$t('PLACEHOLDERS.tax_registration_image')"
            :preSelectedImage="data.tax_registration_image"
            disabled
          />

          <!-- Map Location -->
          <div class="col-12 mb-3">
            <p class="mb-2" style="color: #ef3405; font-size: 16px">
              {{ $t("PLACEHOLDERS.map_location") }}
            </p>
            <MapComponent :lat="data.lat" :lng="data.lng" />
          </div>

          <!-- Statistics Section -->
          <div class="col-12 mb-3 mt-4">
            <h5 class="section-title">
              {{ $t("PLACEHOLDERS.statistics") }}
            </h5>
          </div>

          <!-- Login Count -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.login_count')"
            v-model.trim="data.login_count"
            disabled
          />

          <!-- Last Login At -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.last_login_at')"
            v-model.trim="data.last_login_at"
            disabled
          />

          <!-- Working Hours Section -->
          <div class="col-12 mb-3 mt-4">
            <h5 class="section-title">
              {{ $t("PLACEHOLDERS.working_hours") }}
            </h5>
          </div>

          <div class="col-12">
            <div v-if="workingHours.length > 0" class="working-hours-table">
              <table class="table">
                <thead>
                  <tr>
                    <th>{{ $t("PLACEHOLDERS.day") }}</th>
                    <th>{{ $t("PLACEHOLDERS.from_time") }}</th>
                    <th>{{ $t("PLACEHOLDERS.to_time") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(hour, index) in workingHours" :key="index">
                    <td>{{ hour.day }}</td>
                    <td>{{ hour.from }}</td>
                    <td>{{ hour.to }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-else class="text-muted text-center">
              {{ $t("TABLES.noData") }}
            </p>
          </div>
        </div>
      </form>
    </div>
    <!-- END:: Single Step Form Content -->
  </div>
</template>

<script>
import MapComponent from "@/components/general/MapComponent.vue";

export default {
  name: "ShowProvider",
  components: { MapComponent },

  data() {
    return {
      data: {
        name: "",
        mobile: "",
        email: "",
        avatar: null,
        activity_type: "",
        warehouse_address: "",
        address: "",
        lat: 24.7136,
        lng: 46.6753,
        commercial_registration_image: null,
        tax_registration_image: null,
        created_at: "",
        login_count: "",
        last_login_at: "",
      },
      workingHours: [],
    };
  },

  computed: {
    activityTypeTranslation() {
      const types = {
        logistics_company: this.$t("PLACEHOLDERS.logistics_company"),
        ecommerce_store: this.$t("PLACEHOLDERS.online_store"),
        traditional_store: this.$t("PLACEHOLDERS.traditional_store"),
        individual: this.$t("PLACEHOLDERS.individual"),
      };
      return types[this.data.activity_type] || this.data.activity_type;
    },
  },

  methods: {
    async getDataToShow() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: `providers/${this.$route.params.id}`,
        });

        const providerData = res.data.data;

        // Personal Data
        this.data.name = providerData.name || "";
        this.data.mobile = providerData.mobile || "";
        this.data.email = providerData.email || "";
        this.data.avatar = providerData.avatar?.url || null;
        this.data.created_at = providerData.created_at || "";

        // Provider Info
        if (providerData.provider_info) {
          this.data.commercial_registration_image =
            providerData.provider_info.commercial_registration_image?.url || null;
          this.data.tax_registration_image =
            providerData.provider_info.tax_registration_image?.url || null;
          this.data.activity_type = providerData.provider_info.type || "";
          this.data.warehouse_address =
            providerData.provider_info.warehouse_address || "";
          this.data.address = providerData.provider_info.address || "";
          this.data.lat = parseFloat(providerData.provider_info.lat) || 24.7136;
          this.data.lng = parseFloat(providerData.provider_info.lng) || 46.6753;
        }

        // Update activity type to translated value
        this.data.activity_type = this.activityTypeTranslation;

        // Statistics
        this.data.login_count = providerData.login_count || "0";
        this.data.last_login_at = providerData.last_login_at || this.$t("PLACEHOLDERS.no_data");

        // Working Hours (if available in future API updates)
        this.workingHours = providerData.working_hours || [];
      } catch (error) {
        console.log(error.response?.data?.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
  },

  async created() {
    this.getDataToShow();
  },
};
</script>

<style scoped>
.section-title {
  color: #ef3405;
  font-weight: bold;
  padding-bottom: 10px;
  border-bottom: 2px solid #ef3405;
  margin-bottom: 15px;
}

.working-hours-table {
  width: 100%;
  overflow-x: auto;
}

.working-hours-table .table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

.working-hours-table .table th,
.working-hours-table .table td {
  padding: 12px;
  text-align: center;
  border: 1px solid #ddd;
}

.working-hours-table .table th {
  background-color: #ef3405;
  color: white;
  font-weight: bold;
}

.working-hours-table .table tbody tr:nth-child(even) {
  background-color: #f9f9f9;
}

.working-hours-table .table tbody tr:hover {
  background-color: #f5f5f5;
}
</style>
